
import { supabase } from "@/integrations/supabase/client";
import { format, addMinutes, subMinutes } from "date-fns";
import { parseStoredDateTime } from "@/utils/timeUtils";

export const useTravelBlocks = (serviceId: string | undefined) => {
  const createTravelTimeBlocks = async (bookingId: number) => {
    if (!serviceId) return;
    
    try {
      const { data: booking, error: bookingError } = await supabase
        .from('bookings')
        .select('*')
        .eq('id', bookingId)
        .single();
      
      if (bookingError || !booking) {
        console.error('Error fetching booking for travel blocks:', bookingError);
        return;
      }
      
      const { data: service, error: serviceError } = await supabase
        .from('services')
        .select('travel_time_enabled, travel_duration')
        .eq('id', booking.service_id)
        .single();
      
      if (serviceError || !service || !service.travel_time_enabled) {
        console.log('Travel time not enabled or error:', serviceError);
        return;
      }
      
      const travelMinutes = service.travel_duration === '60' ? 60 : 30;
      const bookingStartTime = parseStoredDateTime(booking.booking_date || booking.start_time, booking.start_time_local);
      const bookingEndTime = parseStoredDateTime(booking.booking_date || booking.end_time, booking.end_time_local);
      
      const { data: allBookings, error: allBookingsError } = await supabase
        .from('bookings')
        .select('*')
        .eq('service_id', parseInt(serviceId))
        .not('id', 'eq', bookingId);
      
      if (allBookingsError) {
        console.error('Error fetching bookings for travel blocks:', allBookingsError);
        return;
      }
      
      const travelToStart = subMinutes(bookingStartTime, travelMinutes);
      const travelToEnd = new Date(bookingStartTime);
      
      const hasPreviousBooking = allBookings.some(b => {
        const bEndTime = parseStoredDateTime(b.booking_date || b.end_time, b.end_time_local);
        return format(bEndTime, 'yyyy-MM-dd HH:mm') === format(travelToStart, 'yyyy-MM-dd HH:mm');
      });
      
      if (!hasPreviousBooking) {
        const { data: conflictsBefore } = await supabase
          .from('bookings')
          .select('*')
          .eq('service_id', parseInt(serviceId))
          .lt('end_time', travelToEnd.toISOString())
          .gt('end_time', travelToStart.toISOString());
        
        if (!conflictsBefore || conflictsBefore.length === 0) {
          await supabase
            .from('bookings')
            .insert({
              service_id: parseInt(serviceId),
              date: format(travelToStart, 'yyyy-MM-dd'),
              start_time: travelToStart.toISOString(),
              end_time: travelToEnd.toISOString(),
              customer_id: booking.customer_id,
              customer_name: 'TRAVEL',
              status: 'travel',
              related_booking_id: bookingId
            });
        }
      }
      
      const travelFromStart = new Date(bookingEndTime);
      const travelFromEnd = addMinutes(bookingEndTime, travelMinutes);
      
      const hasNextBooking = allBookings.some(b => {
        const bStartTime = parseStoredDateTime(b.booking_date || b.start_time, b.start_time_local);
        return format(bStartTime, 'yyyy-MM-dd HH:mm') === format(travelFromEnd, 'yyyy-MM-dd HH:mm');
      });
      
      if (!hasNextBooking) {
        const { data: conflictsAfter } = await supabase
          .from('bookings')
          .select('*')
          .eq('service_id', parseInt(serviceId))
          .lt('start_time', travelFromEnd.toISOString())
          .gt('end_time', travelFromStart.toISOString());
        
        if (!conflictsAfter || conflictsAfter.length === 0) {
          await supabase
            .from('bookings')
            .insert({
              service_id: parseInt(serviceId),
              date: format(travelFromStart, 'yyyy-MM-dd'),
              start_time: travelFromStart.toISOString(),
              end_time: travelFromEnd.toISOString(),
              customer_id: booking.customer_id,
              customer_name: 'TRAVEL',
              status: 'travel',
              related_booking_id: bookingId
            });
        }
      }
    } catch (error) {
      console.error('Error creating travel time blocks:', error);
    }
  };

  return {
    createTravelTimeBlocks
  };
};
